import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

/** Valeurs émises par le composant */
export interface SearchFilterValue {
  searchTerm: string;
  startDate: string;
  endDate: string;
}

@Component({
  selector: 'app-search-filter',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIcon],
  templateUrl: './Searchfilter.component.html',
  styleUrls: ['./Searchfilter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchFilterComponent implements OnInit, OnDestroy {
  // ── Entrées ──────────────────────────────────────────────────────────────

  /** Valeur initiale de la recherche texte */
  @Input() searchTerm: string = '';
  /** Valeur initiale de la date de début (format yyyy-MM-dd) */
  @Input() startDate: string = '';
  /** Valeur initiale de la date de fin (format yyyy-MM-dd) */
  @Input() endDate: string = '';
  /** Délai de debounce pour la recherche texte (ms) */
  @Input() debounceMs: number = 300;
  /** Placeholder du champ de recherche */
  @Input() placeholder: string = 'Rechercher…';

  // ── Sorties ───────────────────────────────────────────────────────────────

  /** Émis à chaque changement de filtre */
  @Output() filterChange = new EventEmitter<SearchFilterValue>();
  /** Émis quand tous les filtres sont réinitialisés */
  @Output() filterReset = new EventEmitter<void>();

  // ── État interne ──────────────────────────────────────────────────────────

  protected _searchTerm: string = '';
  protected _startDate: string = '';
  protected _endDate: string = '';

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // ── Cycle de vie ──────────────────────────────────────────────────────────

  ngOnInit(): void {
    this._searchTerm = this.searchTerm;
    this._startDate = this.startDate;
    this._endDate = this.endDate;

    // Debounce uniquement sur la recherche texte
    this.searchSubject
      .pipe(debounceTime(this.debounceMs), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.emit());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Handlers ──────────────────────────────────────────────────────────────

  onSearchInput(value: string): void {
    this._searchTerm = value;
    this.searchSubject.next(value);
  }

  onDateChange(): void {
    this.emit();
  }

  clearSearch(): void {
    this._searchTerm = '';
    this.emit();
  }

  clearStartDate(): void {
    this._startDate = '';
    this.emit();
  }

  clearEndDate(): void {
    this._endDate = '';
    this.emit();
  }

  resetAll(): void {
    this._searchTerm = '';
    this._startDate = '';
    this._endDate = '';
    this.filterReset.emit();
    this.emit();
  }

  openDatePicker(el: HTMLInputElement | null): void {
    if (!el) return;
    try {
      const anyEl = el as any;
      if (typeof anyEl.showPicker === 'function') {
        anyEl.showPicker();
      } else {
        el.focus();
      }
    } catch {
      try { el.focus(); } catch { /* noop */ }
    }
  }

  // ── Getters ───────────────────────────────────────────────────────────────

  get hasActiveFilters(): boolean {
    return !!this._searchTerm || !!this._startDate || !!this._endDate;
  }

  get activeChips(): { key: 'search' | 'start' | 'end'; label: string; icon: string }[] {
    const chips = [];
    if (this._searchTerm) {
      chips.push({ key: 'search' as const, label: this._searchTerm, icon: 'title' });
    }
    if (this._startDate) {
      chips.push({ key: 'start' as const, label: 'Début : ' + this.formatDateFR(this._startDate), icon: 'event' });
    }
    if (this._endDate) {
      chips.push({ key: 'end' as const, label: 'Fin : ' + this.formatDateFR(this._endDate), icon: 'event' });
    }
    return chips;
  }

  removeChip(key: 'search' | 'start' | 'end'): void {
    if (key === 'search') this.clearSearch();
    if (key === 'start') this.clearStartDate();
    if (key === 'end') this.clearEndDate();
  }

  // ── Privé ─────────────────────────────────────────────────────────────────

  private emit(): void {
    this.filterChange.emit({
      searchTerm: this._searchTerm,
      startDate: this._startDate,
      endDate: this._endDate,
    });
  }

  private formatDateFR(iso: string): string {
    if (!iso) return '';
    try {
      const [y, m, d] = iso.split('-');
      return `${d}/${m}/${y}`;
    } catch {
      return iso;
    }
  }
}